import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Input, Button, Table, Spin, Select, Modal, TreeSelect, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { materialListForAdmin, deleteMaterial, publishMaterial, saveMaterialAccess } from '../../../services/materialsApi';
import { getAllStates, getManagerList, handlePrizeRequest } from '../../../services/adminApi';
import { exportTest } from '../../../services/testApi';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/auth';
import EditMaterialPrizesModal from './EditMaterialPrizesModal';

const { SHOW_PARENT } = TreeSelect;
const { TextArea } = Input;
interface DataType {
  id: number;
  key: string;
  name: string;
  questionsAmount: number;
  isPublished: boolean;
  prize: string;
  access: number[];
  stateId: number;
}

const Materials: React.FC = () => {
  useEffect(() => { getData() }, [])
  const getData = () => {
    setIsLoading(true);
    materialListForAdmin(0).then(data => {
      setMaterials(data);
      setFullData(data);
      setIsLoading(false);
    });
    if ([3, 4].includes(user.role)) getManagerList(0).then(setManagerList);
    if (user.isAdmin) getAllStates().then(setStateList);
  }
  const [materials, setMaterials] = useState<any>([]);
  const [fullData, setFullData] = useState<any>([]);
  const [managerList, setManagerList] = useState<any>([]);
  const [stateList, setStateList] = useState<any>([]);
  const [selectedAccessStates, setSelectedAccessStates] = useState<any>([]);
  const [chosenManagerId, setChosenManagerId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMaterialAccess, setCurrentMaterialAccess] = useState<number | null>(null);
  const [currentMaterial, setCurrentMaterial] = useState<any>(null);
  const [exportingMaterialId, setExportingMaterialId] = useState<number | null>(null);
  const cellInlineStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
  const iconLinkStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', fontSize: 18, lineHeight: 1 };
  const [deletingMaterial, setDeletingMaterial] = useState<number | null>(null);
  const rejectionReason = useRef<string>('');
  let { id = '0' } = useParams();
  const { user } = useAuth();
  useEffect(() => {
    rejectionReason.current = '';
  }, [currentMaterial])

  const handleDelete = (id: number) => {
    setDeletingMaterial(id);
  }

  const confirmDelete = async () => {
    if (!deletingMaterial) return;

    try {
      await deleteMaterial(deletingMaterial);
      getData();
    } catch (error) {
      console.error('Ошибка при удалении материала:', error);
    } finally {
      setDeletingMaterial(null);
    }
  }

  const handlePublish = (id: number, status: boolean) => {
    if (status) {
      const material = materials.find((m: DataType) => m.id === id);
      if (material && material.questionsAmount === 0) {
        Modal.error({
          title: 'Ошибка публикации',
          content: 'Нельзя опубликовать материал без вопросов. Добавьте хотя бы один вопрос для теста.',
        });
        return;
      }
    }

    publishMaterial(id, status)
      .then(() => {
        getData()
      })
      .catch((error) => {
        Modal.error({
          title: 'Ошибка публикации',
          content: error.message || 'Произошла ошибка при публикации материала',
        });
      })
  }

  const handleExportTestFile = async (materialId: number, format: 'docx' | 'xlsx') => {
    try {
      setExportingMaterialId(materialId);
      const { blob, filename } = await exportTest(materialId, format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      Modal.error({
        title: 'Ошибка скачивания',
        content: error?.message || 'Не удалось скачать тест.',
      });
    } finally {
      setExportingMaterialId(null);
    }
  };

  const getExportMenu = (materialId: number): MenuProps => ({
    items: [
      {
        key: 'docx',
        label: 'Скачать Word',
      },
      {
        key: 'xlsx',
        label: 'Скачать Excel',
      },
    ],
    onClick: ({ key }) => handleExportTestFile(materialId, key as 'docx' | 'xlsx'),
  });

  const onSavePrize = (materialId: number, managerId: string, prizes: string) => {
    const tempMaterials = [...materials];
    const currentMaterialIndex = tempMaterials.findIndex(x => x.id === materialId);
    managerId.split(',').forEach(mId => {
      tempMaterials[currentMaterialIndex].prize[mId] = { prizes: prizes, status: 0 };
    })
    setMaterials(tempMaterials);
  }

  const checkStatePermissions = (row: DataType) => {
    if (!user.isAdmin) return false;
    if (!user.extraData?.permissions) return true;
    if (user.type !== 'admin' && row.stateId === 0) return false;

    if (!row.access || row.access.length === 0) {
      return user.type === 'registered' && user.extraData?.permissions?.editUsersAccess;
    }

    return row.access.includes(0) || row.access.includes(user.stateId);
  }

  const canCreateNewMaterial = () => {
    if (!user.isAdmin) return false;
    if (!user.extraData?.permissions) return true;
    return !!user.extraData.permissions.customCategoriesMaterialsTests;
  }

  const getInitialSelectedAccessStates = (row: DataType): number[] => {
    if (user.type === 'registered' && user.extraData?.permissions?.editUsersAccess) {
      if (row.access && row.access.length > 0) {
        if (row.access.includes(0)) {
          return [user.stateId];
        }
        const userAccesses = row.access.filter((stateId: number) => stateId === user.stateId);
        return userAccesses;
      }
      return [];
    }
    return row.access;
  }

  const prizeStatus = ['На согласовании', 'Согласовано', 'Отказано', 'Завершена'];
  const columns: ColumnsType<DataType> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text, row) => {
        const canEdit = checkStatePermissions(row);
        if (!canEdit) return text;
        return (
          <span style={cellInlineStyle}>
            <Link
              style={{ ...iconLinkStyle, marginRight: 6 }}
              to={`/admin/new-material/${row.id}`}
            >
              <EditOutlined />
            </Link>
            <span>{text}</span>
          </span>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Вопросы',
      dataIndex: 'questionsAmount',
      key: 'questionsAmount',
      sorter: (a, b) => a['questionsAmount'] - b['questionsAmount'],
      render: (text, row) => {
        const canEdit = checkStatePermissions(row);
        if (!canEdit) return text;
        const isExporting = exportingMaterialId === row.id;

        return (
          <span style={cellInlineStyle}>
            <span>{text}</span>
            <span style={{ ...cellInlineStyle, marginLeft: 8 }}>
              {user.type === 'admin' && (
                <Dropdown menu={getExportMenu(row.id)} trigger={['click']} placement="bottomRight">
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{
                      ...iconLinkStyle,
                      marginRight: 8,
                      opacity: isExporting ? 0.5 : 1,
                      pointerEvents: isExporting ? 'none' : 'auto'
                    }}
                  >
                    {isExporting ? <LoadingOutlined /> : <DownloadOutlined />}
                  </a>
                </Dropdown>
              )}
              <Link style={iconLinkStyle} to={`/admin/new-test/${row.id}`}>
                <EditOutlined />
              </Link>
            </span>
          </span>
        );
      },
    },
    ...user.isAdmin ?
      [{
        title: 'Доступ',
        dataIndex: 'access',
        key: 'access',
        width: 220,
        render: (item: any, row: DataType) => {
          const accessCount = item?.length || 0;
          const totalStates = stateList.length;
          let displayCount = item?.[0] === 0 ? totalStates : accessCount;

          if (user.type === 'registered' && user.extraData?.permissions?.editUsersAccess) {
            if (accessCount === 0) {
              displayCount = 0;
            } else if (item?.[0] === 0) {
              displayCount = 1;
            } else {
              const userAccessCount = item.filter((stateId: number) => stateId === user.stateId).length;
              displayCount = userAccessCount;
            }
          }

          const canEditAccess = user.type !== 'registered' || user.extraData?.permissions?.hasOwnProperty('editUsersAccess');

          return (
            <span style={cellInlineStyle}>
              <span>{displayCount} / {totalStates}</span>
              {canEditAccess && (
                <Link
                  style={{ ...iconLinkStyle, marginLeft: 8 }}
                  to="#"
                  onClick={() => {
                    setCurrentMaterialAccess(row.id);
                    setSelectedAccessStates(getInitialSelectedAccessStates(row));
                  }}
                >
                  <EditOutlined />
                </Link>
              )}
            </span>
          );
        }
      },
      {
        title: '',
        key: 'id',
        render: (row: any) => {
          const hasAccess = row.access && row.access.length > 0;
          const shouldShowPublishButton = hasAccess ? !row.isPublished : true;
          const buttonText = shouldShowPublishButton ? 'Опубликовать' : 'Заблокировать';
          const buttonTextShort = shouldShowPublishButton ? 'Разб' : 'Блок';
          const buttonClass = shouldShowPublishButton ? 'green-badge' : 'red-badge';

          return checkStatePermissions(row) &&
            <Row gutter={[0, 5]} style={{ maxWidth: 190, float: 'right' }}>
              <Col>
                <Col xs={24} sm={0}><span onClick={() => handlePublish(row.id, shouldShowPublishButton)} className={buttonClass}>{buttonTextShort}</span></Col>
                <Col xs={0} sm={24}><span onClick={() => handlePublish(row.id, shouldShowPublishButton)} className={buttonClass}>{buttonText}</span></Col>
              </Col>
              {user.type !== 'registered' && <Col><span onClick={() => handleDelete(row.id)} style={{ color: '#A40207', cursor: 'pointer', marginLeft: 15 }}><DeleteOutlined style={{ fontSize: 18 }} /></span></Col>}
            </Row>
        }
      }] : [],
  ];

  const handleSearch = (e: any) => {
    const value = e.target.value.toLowerCase();
    if (value.length > 2) setMaterials(fullData.filter((x: DataType) => x.name.toLowerCase().indexOf(value) >= 0))
    else setMaterials(fullData)
  }

  const handlePrize = (status: number, material: any = null) => {
    const handledMaterial = material || currentMaterial;
    const tempMaterials = [...materials];
    const currentMaterialIndex = tempMaterials.findIndex(x => x.id === handledMaterial.id);
    let currentPrizeIndex = chosenManagerId;
    if (!chosenManagerId) {
      const temp = Object.values(tempMaterials[currentMaterialIndex].prize) as any[];
      currentPrizeIndex = temp.filter((x: any) => !x.status)?.[1]?.manager?.id
    }
    handlePrizeRequest(handledMaterial.prize[currentPrizeIndex].id, status, rejectionReason.current);
    setCurrentMaterial(null);
    tempMaterials[currentMaterialIndex].prize[currentPrizeIndex].status = status;
    setMaterials(tempMaterials);
  }

  const handleAccess = () => {
    const isAllStatesSelected = Array.isArray(selectedAccessStates) && selectedAccessStates.includes(0);
    let selectedState = isAllStatesSelected ? [0] : selectedAccessStates;

    if (user.type === 'registered' && user.extraData?.permissions?.editUsersAccess) {
      if (selectedAccessStates.length === 0) {
        selectedState = [];
      } else {
        selectedState = selectedAccessStates;
      }
    }

    if (currentMaterialAccess) {
      saveMaterialAccess(currentMaterialAccess, selectedState)
        .then(() => {
          const tempMaterials = [...materials];
          const currentMaterialIndex = tempMaterials.findIndex(x => x.id === currentMaterialAccess);
          const currentMaterial = tempMaterials[currentMaterialIndex];

          if (user.type === 'registered' && user.extraData?.permissions?.editUsersAccess) {
            const hasAllStatesAccess = currentMaterial?.access?.includes(0);

            if (selectedState.length === 0) {
              if (hasAllStatesAccess) {
                const allStatesExceptUser = stateList
                  .map((state: any) => state.id)
                  .filter((stateId: number) => stateId !== user.stateId);
                tempMaterials[currentMaterialIndex].access = allStatesExceptUser;
              } else {
                const currentAccesses = currentMaterial?.access || [];
                tempMaterials[currentMaterialIndex].access = currentAccesses.filter((stateId: number) => stateId !== user.stateId);
              }
            } else {
              if (hasAllStatesAccess) {
                tempMaterials[currentMaterialIndex].access = [0];
              } else {
                const currentAccesses = currentMaterial?.access || [];
                if (!currentAccesses.includes(user.stateId)) {
                  tempMaterials[currentMaterialIndex].access = [...currentAccesses, user.stateId];
                } else {
                  tempMaterials[currentMaterialIndex].access = currentAccesses;
                }
              }
            }
          } else {
            tempMaterials[currentMaterialIndex].access = selectedState;
          }

          setMaterials(tempMaterials);
        })
        .catch((error) => {
          console.error('Ошибка при сохранении доступов:', error);
          Modal.error({
            title: 'Ошибка',
            content: 'Не удалось сохранить доступы. Попробуйте позже.',
          });
        });
    }

    setCurrentMaterialAccess(null);
  }

  const getTreeData = () => {
    if (user.type === 'registered' && user.extraData?.permissions?.editUsersAccess) {
      const userState = stateList.find((state: any) => state.id === user.stateId);
      return [{
        title: userState?.name,
        value: user.stateId,
        key: user.stateId
      }];
    }

    return [{
      title: 'Все филиалы',
      value: 0,
      key: 0,
      children: stateList.map((x: any) => ({ label: x.name, value: x.id }))
    }];
  };

  const defaultCurrent = Math.ceil((materials.findIndex((x: any) => x.id === Number(id)) + 1) / 10);
  return (
    <Spin spinning={isLoading}>
      <Row gutter={[16, 16]} style={{ marginTop: 40 }} >
        <Col span={24} style={{ fontSize: 25 }}>
          Материалы
        </Col>
        {!!managerList.length &&
          <Col sm={8} xs={12}>
            <Select
              style={{ width: '100%', height: 35 }}
              defaultValue={0}
              options={[{ label: 'Все менеджеры', value: 0 }, ...managerList.map((x: any) => ({ value: x.id, label: x.name }))]}
              onChange={setChosenManagerId}
            />
          </Col>
        }
        <Col sm={canCreateNewMaterial() ? 16 : 24} xs={canCreateNewMaterial() ? 12 : 24}><Input style={{ height: 35 }} onChange={handleSearch} placeholder='Поиск по названию (мин 3 символа)' /></Col>
        {canCreateNewMaterial() &&
          <Col sm={8} xs={24}><Link to={'/admin/new-material'}><Button style={{ width: '100%' }}> Новый материал <PlusOutlined /></Button></Link></Col>
        }
        {!!materials.length && <Col span={24}><Table columns={columns} dataSource={materials} pagination={{
          defaultCurrent: defaultCurrent > 0 ? defaultCurrent : 1,
          showSizeChanger: true,
          locale: {
            items_per_page: 'стр',
            jump_to: 'Перейти',
            jump_to_confirm: 'OK',
            page: 'стр',
          }
        }} /></Col>}
      </Row>
      <Modal title={'Уверены что хотите отклонить?'} open={!!currentMaterial} onOk={() => handlePrize(2)} onCancel={() => setCurrentMaterial(null)}>
        <TextArea rows={4} onChange={(e) => rejectionReason.current = e.target.value} />
      </Modal>
      {!!currentMaterialAccess && <Modal title={'Редактирование доступов'} open={true} onOk={() => handleAccess()} onCancel={() => setCurrentMaterialAccess(null)} okText="Ок" cancelText="Отмена">
        <TreeSelect
          treeCheckable={true}
          allowClear
          showCheckedStrategy={SHOW_PARENT}
          style={{ width: '100%' }}
          placeholder="Please select"
          defaultValue={selectedAccessStates}
          onChange={(value) => setSelectedAccessStates(value)}
          treeData={getTreeData()}
          treeDefaultExpandAll
        />
      </Modal>}
      <Modal
        title="Удаление материала"
        open={!!deletingMaterial}
        onOk={confirmDelete}
        onCancel={() => setDeletingMaterial(null)}
        okText="Удалить"
        cancelText="Отмена"
      >
        <p>Вы уверены, что хотите удалить этот материал?</p>
      </Modal>
    </Spin>
  );
};

export default Materials;
